/**
 * Launcher V2 A2 U5 — pure `?step=` query-param logic for `PlayFlowShell`'s
 * freely-navigable 3-step stepper (plan Key Decision 1: URL state, not
 * nested routes with per-step guards). Factored out of the component so it
 * is unit-testable without a DOM — `mini-apps/launcher` has no jsdom/
 * happy-dom harness (see `__tests__/v2-push-drawer.test.tsx`'s head-of-file
 * caveat, applied identically here).
 */

export type PlayFlowStepId = 'qualify' | 'sequences' | 'review';

export interface PlayFlowStepDescriptor {
  id: PlayFlowStepId;
  label: string;
}

/** Order matters — this IS the stepper's left-to-right order (Qualify / Sequences / Review). */
export const PLAY_FLOW_STEPS: readonly PlayFlowStepDescriptor[] = [
  { id: 'qualify', label: 'Qualify' },
  { id: 'sequences', label: 'Sequences' },
  { id: 'review', label: 'Review' },
];

const VALID_STEP_IDS = new Set<string>(PLAY_FLOW_STEPS.map((s) => s.id));

export const DEFAULT_PLAY_FLOW_STEP: PlayFlowStepId = 'qualify';

/**
 * Batch 9 B1 — "Audience Rules" is a DISABLED 4th tab rendered after Review,
 * behind a "Coming soon" tooltip (plan Decision #3: the dormant
 * preview-coupling in `AudienceRulesStep` stays intact but inert). Its id is
 * deliberately kept OUT of {@link PLAY_FLOW_STEPS} / {@link VALID_STEP_IDS} —
 * a deep-link `?step=audience-rules` must fall back to
 * {@link DEFAULT_PLAY_FLOW_STEP} exactly like any other unrecognized value,
 * because the tab isn't actually navigable yet. `PlayFlowShell` renders these
 * separately from the real stepper, as disabled `Tab`s with a tooltip, and
 * mounts a matching but unreachable `TabPanel` for each. Re-enabling one
 * later is meant to be a one-line move: relocate its descriptor from this
 * list into `PLAY_FLOW_STEPS` and drop the `disabled`/`Tooltip` wrapper in
 * `PlayFlowShell.tsx` — the hosted component itself needs no rewrite.
 */
export type ComingSoonTabId = 'audience-rules';

export interface ComingSoonTabDescriptor {
  id: ComingSoonTabId;
  label: string;
}

export const COMING_SOON_TABS: readonly ComingSoonTabDescriptor[] = [
  { id: 'audience-rules', label: 'Audience Rules' },
];

/**
 * Parses the raw `?step=` value. Any missing, malformed, or unrecognized
 * value falls back to {@link DEFAULT_PLAY_FLOW_STEP} — never throws, and
 * never leaves the shell on a step nothing renders for.
 */
export function parsePlayFlowStep(raw: string | null | undefined): PlayFlowStepId {
  if (raw && VALID_STEP_IDS.has(raw)) return raw as PlayFlowStepId;
  return DEFAULT_PLAY_FLOW_STEP;
}

/**
 * The `?step=` value to write back for a given step id. Identity today (the
 * id IS the wire value), but kept as a named function so the query-param
 * format stays decoupled from the internal type — if the wire vocabulary
 * ever needs to diverge from `PlayFlowStepId`, only this function changes.
 */
export function playFlowStepParam(step: PlayFlowStepId): string {
  return step;
}

/**
 * Round-3c QA fixes, H2 — the `?rung=` query param a "Where is this used"
 * deep link (`UsagePlacesList`, H1) carries, so `SequencesStep`/`QualifyStep`
 * can preselect the exact rung the rep clicked through from. A rung id is
 * just an opaque non-empty string here — nothing in `play-flow-step.ts`
 * validates it against a real ladder (that lookup needs `railRungs`, which
 * this module doesn't have); an id that doesn't match anything is the
 * caller's "unknown rung id → ignore silently" case, not this parser's.
 *
 * Missing, empty, or whitespace-only → `null` — never throws, matching
 * `parsePlayFlowStep`'s own never-throw contract.
 */
export function parsePlayFlowRungParam(raw: string | null | undefined): string | null {
  if (raw && raw.trim().length > 0) return raw;
  return null;
}
